import React, { FunctionComponent, useEffect, useState } from 'react';

import clsx from 'clsx';

import { nonBreakableSpace as nbsp } from '@utils/constants';
import { isValueSet } from '@utils/guards';
import { roundNumber } from '@utils/utils';

import { useDebounce } from '@hooks/use-debounce';

import { CurrencyPresenter } from '@components/common/CurrencyPresenter/CurrencyPresenter';
import { MinusIcon, PlusIcon } from '@components/icons';

import styles from './QuantityPicker.module.scss';

const DEBOUNCE_DELAY = 500;

export const QUANTITY_PICKER_LIMITS = {
    max: 500,
    min: 1,
};

const maxInitialQuantity = (quantity: number): number =>
    quantity > QUANTITY_PICKER_LIMITS.max ? QUANTITY_PICKER_LIMITS.max : quantity;

type ProductCardProps = {
    initialQuantity?: number;
    onDebounceQuantityChange?(debouncedQuantity: number, onCatch: VoidFunction): void;
    onRemoveAtZeroQuantity?(): void;
    step?: number;
    price?: FeipApi.Kopecks;
    unit?: string;
    isDisabledInterface?: boolean;
    className?: string;
};

export const QuantityPicker: FunctionComponent<ProductCardProps> = (props) => {
    const {
        price,
        onDebounceQuantityChange,
        onRemoveAtZeroQuantity,
        className,
        initialQuantity = 1,
        step = 1,
        unit = 'шт.',
        isDisabledInterface = false,
    } = props;

    const [isInitialized, setIsInitialized] = useState(false);
    const [isUpdatedFromInitial, setIsUpdatedFromInitial] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);
    const [quantity, setQuantity] = useState(maxInitialQuantity(initialQuantity));
    const [debouncedQuantity] = useDebounce(quantity, DEBOUNCE_DELAY);

    const isMinQuantity = quantity <= QUANTITY_PICKER_LIMITS.min;
    const isMaxQuantity = quantity >= QUANTITY_PICKER_LIMITS.max;

    const isDisabledDecrementButton = !isValueSet(onRemoveAtZeroQuantity) && isMinQuantity;
    const isDisabledIncrementButton = isMaxQuantity;

    const setInitialQuantity = () => {
        setIsUpdatedFromInitial(true);
        setQuantity(maxInitialQuantity(initialQuantity));
    };

    useEffect(() => {
        if (isInitialized && initialQuantity !== debouncedQuantity) setInitialQuantity();
    }, [initialQuantity]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (
            isInitialized &&
            !isUpdatedFromInitial &&
            !isRemoving &&
            isValueSet(onDebounceQuantityChange) &&
            debouncedQuantity > 0
        ) {
            onDebounceQuantityChange(debouncedQuantity, setInitialQuantity);
        }
        setIsUpdatedFromInitial(false);
        setIsRemoving(false);
    }, [debouncedQuantity]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setIsInitialized(true);
    }, []);

    const setIncrement = () => {
        if (isDisabledIncrementButton) return;

        if (quantity < QUANTITY_PICKER_LIMITS.max) setQuantity((value) => value + 1);
    };

    const setDecrement = () => {
        if (isDisabledDecrementButton) return;

        if (isValueSet(onRemoveAtZeroQuantity) && isMinQuantity) {
            onRemoveAtZeroQuantity();
            setIsRemoving(true);
            return;
        }

        if (quantity > QUANTITY_PICKER_LIMITS.min) {
            setQuantity((value) => value - 1);
        }
    };

    const totalPrice: FeipApi.Kopecks | null =
        price !== undefined ? ((quantity * price) as FeipApi.Kopecks) : null;

    const presentQuantity = roundNumber(quantity * step, 2);

    const formattedQuantity = `${presentQuantity}${nbsp}${unit}${nbsp}`;

    return (
        <div className={clsx(styles.root, className)}>
            <button
                type="button"
                className={clsx(styles.button, isDisabledInterface && styles.isDisabledInterface)}
                aria-label="Уменьшить количество"
                onClick={setDecrement}
                disabled={isDisabledDecrementButton}
            >
                <MinusIcon className={styles.icon} />
            </button>

            <span className={styles.value}>
                <span className={styles.count}>
                    {formattedQuantity}
                    {totalPrice !== null && <span className={styles.totalPriceText}>/</span>}
                </span>
                {totalPrice !== null && (
                    <span className={styles.totalPriceText}>
                        <CurrencyPresenter amount={totalPrice} />
                    </span>
                )}
            </span>

            <button
                type="button"
                className={clsx(styles.button, isDisabledInterface && styles.isDisabledInterface)}
                aria-label="Увеличить количество"
                onClick={setIncrement}
                disabled={isDisabledIncrementButton}
            >
                <PlusIcon className={clsx(styles.icon, styles.isPlus)} />
            </button>
        </div>
    );
};
